import { useEffect, useState } from 'react';
import { browserHistory } from 'react-router';

import auth from './auth';
import ajax, { PortalUser } from './ajax';
import { PortalUser as EnumPortalUser } from '@playbook-technologies/common-enums';
import Header from './Header';
import InfluencerSelect from './InfluencerSelect';
import StatusBar from './StatusBar';
import { navigate, PATHS } from '@client/routes';
import { Typography } from '@mui/material';
import { PrimaryButton, SecondaryButton } from '@client/new/components/button';

type PortalUserDraft = Partial<Omit<PortalUser, 'id' | 'influencerId'>> & {
  id?: number | null;
  influencerId?: number | null;
};

const EMPTY_PORTAL_USER: PortalUserDraft = {
  id: null,
  firstName: '',
  lastName: '',
  email: '',
  role: EnumPortalUser.Role.Moderator.value,
};

const validatePortalUser = (data) => {
  const validationResult = {
    passed: false,
    messages: [] as string[],
  };
  if (data.role === EnumPortalUser.Role.Influencer.value) {
    if (!data.influencerId || parseInt(data.influencerId) < 1) {
      validationResult.messages.push(
        'Users with the Creator role require a selected Partner.',
      );
    }
  }
  if (!data.email || data.email.length < 1) {
    validationResult.messages.push('E-mail address is required.');
  }
  if (validationResult.messages.length === 0) {
    validationResult.passed = true;
  }
  return validationResult;
};

const PortalUserEdit = (props) => {
  const [portalUser, setPortalUser] =
    useState<PortalUserDraft>(EMPTY_PORTAL_USER);

  const setInput = (e) => {
    const { name, value } = e.target;
    setPortalUser((prev) => {
      const next = { ...prev, [name]: value };
      if (auth.isModerator() && (next.firstName || next.lastName)) {
        next.username = `${next.firstName}${next.lastName}`
          .replace(/\s/g, '')
          .toLowerCase();
      }
      return next;
    });
  };

  const getPortalUserData = () => {
    if (props.params.id === 0 || props.params.id === '0') {
      setPortalUser({ ...EMPTY_PORTAL_USER, isActive: true });
      return;
    }
    ajax
      .getPortalUsers(props.params.id)
      .then((pu) => {
        setPortalUser(pu);
      })
      .catch((e) => {
        if (e === 'auth') {
          auth.logout();
          return;
        }
        setPortalUser(EMPTY_PORTAL_USER);
        StatusBar.sendErrorMessage(
          'You may be experiencing connectivity issues.',
        );
      });
  };

  useEffect(() => {
    getPortalUserData();
  }, []);

  const save = (postData) => {
    ajax
      .upsertPortalUser(postData)
      .then(() => {
        navigate(PATHS.PORTAL_USERS);
      })
      .catch((e) => {
        if (e === 'auth') {
          auth.logout();
        }
        StatusBar.sendErrorMessage(
          e?.response?.data ??
            e?.message ??
            'An error occured. Please check your data and try again.',
        );
      });
  };

  const goBack = (e) => {
    e.preventDefault();
    browserHistory.goBack();
  };

  const submit = (e) => {
    e.preventDefault();

    const validationResults = validatePortalUser(portalUser);
    if (validationResults.passed) {
      save({ ...portalUser, influencerId: portalUser.influencerId ?? null });
    } else {
      StatusBar.sendErrorMessage(validationResults.messages.join(' '));
    }
  };

  const merge = (newPostData) => {
    setPortalUser((prev) => ({ ...prev, ...newPostData }));
  };

  const roleChanged = (data) => {
    merge({
      role: data.role,
      influencerId: data.influencerId,
    });
  };

  const title =
    portalUser && portalUser.firstName
      ? `${portalUser.firstName} ${portalUser.lastName}`
      : 'New User';

  if (!portalUser) {
    return <span></span>;
  }

  return (
    <div className="portalUserEdit">
      <Header>
        <Typography variant="h1">{title}</Typography>
        <div className="box">
          <div className="button-box flex gap-5">
            <PrimaryButton onClick={submit}>Save</PrimaryButton>
            <SecondaryButton onClick={goBack}>Cancel</SecondaryButton>
          </div>
        </div>
      </Header>
      <main id="main">
        <div>
          <br />
          <div className="general-settings">
            <div className="data-form-holder">
              <div className="data-row row-6">
                <div
                  className="input-holder"
                  style={{ width: auth.isModerator() ? '100%' : '50%' }}
                >
                  <label>First Name</label>
                  <input
                    type="text"
                    onChange={setInput}
                    name="firstName"
                    value={portalUser.firstName}
                    placeholder="First name"
                  />
                </div>
                <div
                  className="input-holder"
                  style={{ width: auth.isModerator() ? '100%' : '50%' }}
                >
                  <label>Last Name</label>
                  <input
                    type="text"
                    onChange={setInput}
                    name="lastName"
                    value={portalUser.lastName}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <RoleEdit
                role={portalUser.role}
                influencerId={portalUser.influencerId}
                onChange={roleChanged}
              />
              <div className="data-block">
                <div className="input-holder">
                  <label>E-mail:*</label>
                  <input
                    type="text"
                    onChange={setInput}
                    name="email"
                    value={portalUser.email}
                    placeholder="Email"
                  />
                </div>
              </div>
            </div>
            <p style={{ color: '#f00' }}></p>
          </div>
        </div>
      </main>
      <StatusBar.Component />
    </div>
  );
};

export default PortalUserEdit;

const RoleEdit = (props) => {
  const roleChanged = (ev) => {
    const role = parseInt(ev.target.value);
    let influencerId = props.influencerId;
    if (role !== EnumPortalUser.Role.Influencer.value) {
      influencerId = null;
    }
    props.onChange({ role, influencerId });
  };

  const influencerChanged = (option) => {
    props.onChange({
      role: props.role,
      influencerId: option?.value ?? null,
    });
  };

  const isInfluencer = props.role == EnumPortalUser.Role.Influencer.value;

  return (
    <div className="data-row row-6">
      <div className="input-holder">
        <label>Role</label>
        <select value={props.role} onChange={roleChanged}>
          {auth.isAdministrator() ? (
            <option key="1" value={EnumPortalUser.Role.Administrator.value}>
              Super Admin
            </option>
          ) : (
            ''
          )}
          <option key="2" value={EnumPortalUser.Role.Moderator.value}>
            Admin
          </option>
          <option key="3" value={EnumPortalUser.Role.Influencer.value}>
            Creator
          </option>
          <option key="4" value={EnumPortalUser.Role.ContentEditor.value}>
            Content Editor
          </option>
        </select>
      </div>
      {isInfluencer ? (
        <div className="input-holder">
          <label>Related Partner</label>
          <div className="absolute w-96">
            <InfluencerSelect
              influencerId={props.influencerId}
              onChange={influencerChanged}
            />
          </div>
        </div>
      ) : (
        <div className="input-holder"></div>
      )}
    </div>
  );
};
